import { useState, useCallback, useEffect, useRef, forwardRef } from "react"
import { useDropzone } from "react-dropzone"
import { Button } from "components/ui/button"
import { Card, CardContent, CardFooter } from "components/ui/card"
import SparklesText from "components/magicui/sparkles-text"
import { Upload } from "lucide-react"
import styles from "./style.module.css"
import Layout from "components/layout/Layout"
import VideoUploadPage from "./VideoUploader"
import TextEditor from "./TextEditor"
import VisualEditor from "./VisualEditor"
import axiosInstance from "axiosInstance"
import { useNavigate, useParams } from "react-router-dom"
import {
  uploadFile,
  validateAndCorrectFileName,
  validateDuration,
  validateFileSize,
  validateFileType,
  getVideoDimensions,
  isStandardSize,
} from "utils"
import { useToast } from "components/ui/toaster/use-toast"
import { Toaster } from "components/ui/toaster/toaster"

const steps = [
  {
    id: "upload",
    title: "Upload Video",
    component: VideoUploadPage,
  },
  {
    id: "chunk",
    title: "Chunk Editor",
    component: forwardRef((props, ref) => <TextEditor ref={ref} {...props} />),
  },
  {
    id: "edit",
    title: "Video Editor",
    component: forwardRef((props, ref) => <VisualEditor ref={ref} {...props} />),
  },
]

export default function MultiStepVideoEditor({ initialData = null }) {
  const [currentStep, setCurrentStep] = useState(0)
  const [file, setFile] = useState(null)
  const [activeTab, setActiveTab] = useState("video")
  const [sceneData, setSceneData] = useState(null)
  const [videoID, setVideoID] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const { toast } = useToast()
  const [hasAcknowledgedResolutionWarning, setHasAcknowledgedResolutionWarning] = useState(false)
  const [subtitles, setSubtitles] = useState(true)
  const navigate = useNavigate()
  const textEditorRef = useRef(null)
  const visualEditorRef = useRef(null)
  const params = useParams()
  useEffect(() => {
    if (initialData) {
      setSceneData(initialData)
      setCurrentStep(initialData.step ? initialData.step : 0)
      setVideoID(params.videoID ? params.videoID : null)
    }
  }, [initialData])

  useEffect(() => {
    console.log("main videoID", videoID)
  }, [videoID])

  const nextStep = async () => {
    setIsLoading(true)
    try {
      if (currentStep === 0) {
        if (!file) {
          console.log("No file selected")
          setIsLoading(false)
          return
        }
        if (!validateFileType(file)) {
          alert("Invalid file type. Please upload a video file.")
          setIsLoading(false)
          return
        }
        if (!validateFileSize(file)) {
          alert("File size exceeds the limit. Please upload a smaller file.")
          setIsLoading(false)
          return
        }
        const isDurationValid = await validateDuration(file)
        if (!isDurationValid) {
          alert("Video duration exceeds the limit. Please upload a shorter video.")
          setIsLoading(false)
          return
        }
        const dimensions = await getVideoDimensions(file)
        if (!isStandardSize(dimensions)) {
          if (!hasAcknowledgedResolutionWarning) {
            toast({
              title: "Invalid video resolution",
              description:
                "Supported resolutions are 720p, 1080p, and 4K. If you submit a video with a different resolution, it will be automatically resized.",
            })
            setHasAcknowledgedResolutionWarning(true)
            setIsLoading(false)
            return
          }
        }

        const validFileName = validateAndCorrectFileName(file)
        const correctedFile = new File([file], validFileName, {
          type: file.type,
        })

        const link = await uploadFile(correctedFile, "video-container-cinematic", 'video')
        console.log("link", link)
        const data = { video: link }
        const config = { headers: { "content-type": "application/json" } }
        const response = await axiosInstance.post(
          process.env.REACT_APP_BACKEND_URL + "/microservices/marketier/generate_chunks/",
          data,
          config,
        )
        console.log(response.data)
        const id = response.data.video_id
        console.log("id", id)
        setVideoID(id)
      }

      if (currentStep === 1) {
        if (textEditorRef.current?.handleSave) {
          await textEditorRef.current.handleSave()
        }
        await create_scenejson()
      }

      if (currentStep === 2) {
        if (visualEditorRef.current?.updateSceneJson) {
          await visualEditorRef.current.updateSceneJson()
        }
        await create_video()
        setIsLoading(false)
        return
      }

      setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1))
    } catch (error) {
      console.error("Error during step processing:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 0))

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0])
      setHasAcknowledgedResolutionWarning(false)
    }
  }, [])

  const create_video = async () => {
    if (!videoID) {
      alert("Please upload the video first")
      setCurrentStep(0)
      return
    }
    console.log("videoID", videoID)
    const config = {
      headers: { "content-type": "application/json" },
    }

    const response = await axiosInstance.post(
      process.env.REACT_APP_BACKEND_URL + `/microservices/marketier/create_video`,
      { video_id: videoID },
      config,
    )

    const output_id = response.data.video_id
    console.log("video_id", output_id)
    navigate(`/result/microservices/${output_id}`)
    console.log(response.data)
  }

  const create_scenejson = async () => {
    if (!videoID) {
      alert("Please upload the video first")
      return
    }

    const data = {
      video_id: videoID,
    }
    const config = {
      headers: { "content-type": "application/json" },
    }

    const response = await axiosInstance.post(
      process.env.REACT_APP_BACKEND_URL + `/microservices/marketier/create_scenejson/`,
      data,
      config,
    )

    console.log(response.data)
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "video/*": [] },
    multiple: false,
    noClick: true,
  })

  const CurrentStepComponent = steps[currentStep].component

  return (
    <Layout headerStyle={2} footerStyle={2}>
      <div {...getRootProps()}>
        <input {...getInputProps()} />
        {isDragActive && (
          <div className="fixed inset-0 bg-blue-400 bg-opacity-50 z-50 flex items-center justify-center">
            <div className="aspect-square border-2 border-dashed rounded-3xl p-8 text-center cursor-pointer transition-colors h-64 flex flex-col items-center justify-center border-blue-400 bg-blue-200 shadow-lg">
              <Upload className="mx-auto text-blue-500 mb-4" size={48} />
              <p className="text-lg text-blue-500">Drop the video file here ...</p>
            </div>
          </div>
        )}

        {/* Loading overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="relative w-64 h-64 flex items-center justify-center">
              {/* Subtle glow effect behind logo */}
              <div className="absolute inset-0 rounded-full bg-white dark:bg-blue-400 filter blur-xl opacity-20 animate-pulse-subtle"></div>

              {/* Rotating logo - direct without container */}
              <div className="relative z-10 animate-spin-slow">
                {/* Replace with your actual logo */}
                <img
                  src="https://assets-editai.azureedge.net/images/editai-favicon.png"
                  alt="Edit AI Logo"
                  className="object-contain w-56 h-56"
                />
              </div>

              {/* Static text below the rotating logo */}
              <div className="absolute -bottom-12 w-full text-center">
                <p className="text-lg font-semibold text-white">Cooking👨‍🍳...</p>
              </div>
            </div>
          </div>
        )}

        <div className="container mx-auto p-2 md:p-4 max-w-6xl">
          <Card className="border-0 shadow-none">
            <div className="border-gray-200 dark:border-gray-700">
              <SparklesText
                text="Marketers Suite 🎬"
                className={`font-display text-center text-6xl font-extrabold tracking-[-0.02em] drop-shadow-sm dark:text-white md:text-4xl md:leading-[5rem] ${styles.pageHeading}`}
              />
            </div>
            <CardContent>
              {/* Breadcrumbs */}
              <nav className="flex justify-center overflow-x-auto py-2" aria-label="Breadcrumb">
                <ol className="inline-flex items-center space-x-1 md:space-x-3 min-w-max">
                  {steps.map((step, index) => (
                    <li key={step.id} className="inline-flex items-center">
                      {index > 0 && (
                        <svg
                          className="w-3 h-3 mx-1 text-gray-400"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 6 10"
                        >
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="m1 9 4-4-4-4"
                          />
                        </svg>
                      )}
                      <Button
                        variant={"ghost"}
                        className={`inline-flex items-center text-xs md:text-sm font-medium px-2 md:px-3 ${
                          currentStep === index ? "text-blue-600" : "text-gray-700 hover:text-blue-600"
                        }`}
                        onClick={() => setCurrentStep(index)}
                      >
                        {step.title}
                      </Button>
                    </li>
                  ))}
                </ol>
              </nav>

              {/* Current Step Component */}
              <div className="mt-6">
                <CurrentStepComponent
                  ref={currentStep === 1 ? textEditorRef : currentStep === 2 ? visualEditorRef : null}
                  file={file}
                  setFile={setFile}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  sceneData={sceneData}
                  setVideoID={setVideoID}
                  videoID={videoID}
                  setCurrentStep={setCurrentStep}
                  subtitles={subtitles}
                  setSubtitles={setSubtitles}
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col sm:flex-row justify-between space-y-2 sm:space-y-0">
              <Button onClick={prevStep} disabled={currentStep === 0} variant="outline">
                Previous
              </Button>
              <Button onClick={nextStep} variant="default" className="bg-blue-600 text-white">
                {currentStep === steps.length - 1 ? "Finish" : "Next"}
              </Button>
            </CardFooter>
          </Card>
        </div>
        <Toaster />
      </div>
    </Layout>
  )
}
